import { FormikProps } from 'formik';
import { ButtonToggles } from '@eika/radio-button';
import { CheckboxGroup } from '@eika/checkbox';
import { ButtonTypes, StandardButton } from '@eika/button';
import React from 'react';
import { ExpandableInput } from '@eika/expandable-input';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import InputLabel from '@eika/input/src/InputLabel';
import { CustomerType, NAERING, NjffValues, Page, PRIVAT } from '@/data/Types';
import ErrorSubmittingMessage from '@/components/ErrorSubmittingMessage';
import FormFieldErrorMessage from '@/components/FormFieldErrorMessage';
import { InsuranceFieldNames } from '@/forms/fields/insurance';

export enum Forsikring {
    JAKTHUND = 'Jakthundforsikring',
    VERDIGJENSTAND = 'Verdigjendstandforsikring',
    PERSON = 'Personforsikring',
    INNBO = 'Innboforsikring',
    BIL = 'Bilforsikring',
    HYTTE = 'Hytteforsikring',
    HUS = 'Husforsikring',
    REISE = 'Reiseforsikring',
    ANNET = 'Annet',
}

const Insurances = ({
    formik,
    setPage,
    sentError,
    removeSentError,
}: {
    formik: FormikProps<NjffValues>;
    setPage: (page: Page) => void;
    sentError: boolean;
    removeSentError: () => void;
}) => {
    const removeFieldErrorMessage = (field: InsuranceFieldNames) => formik.setFieldTouched(field, false);
    const setFieldValue = (field: InsuranceFieldNames, value: string | string[]) => formik.setFieldValue(field, value);

    const isChecked = (name: Forsikring): boolean => {
        return formik.values.insurances?.indexOf(name) >= 0;
    };

    const setChecked = (checked: boolean, value: string) => {
        if (!checked) {
            setFieldValue(
                'insurances',
                formik.values.insurances.filter((v: string) => v !== value),
            );
        } else {
            setFieldValue('insurances', [...formik.values.insurances, value]);
        }

        removeSentError();
        removeFieldErrorMessage('insurances');
    };

    const onTilbakeClick = () => {
        removeSentError();
        setPage('personalInfo');
    };

    const onChangeCustomerType = (customerType: CustomerType) => {
        setFieldValue('type', customerType);
        setFieldValue('insurances', formik.initialValues.insurances);

        removeSentError();
        removeFieldErrorMessage('type');
    };

    return (
        <>
            <h1 className="form-title">Meld interesse til Fremtind</h1>
            <div className="form-container">
                <div className="input-field">
                    <ButtonToggles
                        label="Trenger du privat forsikring, eller er det i forbindelse med næring/landbruk?"
                        options={[
                            { label: 'Privat', value: PRIVAT },
                            { label: 'Næring/landbruk', value: NAERING },
                        ]}
                        onChange={(customerType: string) => onChangeCustomerType(customerType as CustomerType)}
                        selectedOption={formik.values.type}
                    />
                    <FormFieldErrorMessage formik={formik} field="type" />
                    {formik.values.type === PRIVAT && (
                        <div className="form-field">
                            <CheckboxGroup
                                label="Hvilke forsikringer er du interessert i?"
                                options={[
                                    {
                                        value: Forsikring.JAKTHUND,
                                        label: Forsikring.JAKTHUND,
                                        onValueChange: setChecked,
                                        checked: isChecked(Forsikring.JAKTHUND),
                                    },
                                    {
                                        value: Forsikring.VERDIGJENSTAND,
                                        label: Forsikring.VERDIGJENSTAND,
                                        onValueChange: setChecked,
                                        checked: isChecked(Forsikring.VERDIGJENSTAND),
                                    },
                                    {
                                        value: Forsikring.PERSON,
                                        label: Forsikring.PERSON,
                                        onValueChange: setChecked,
                                        checked: isChecked(Forsikring.PERSON),
                                    },
                                    {
                                        value: Forsikring.INNBO,
                                        label: Forsikring.INNBO,
                                        onValueChange: setChecked,
                                        checked: isChecked(Forsikring.INNBO),
                                    },
                                    {
                                        value: Forsikring.BIL,
                                        label: Forsikring.BIL,
                                        onValueChange: setChecked,
                                        checked: isChecked(Forsikring.BIL),
                                    },
                                    {
                                        value: Forsikring.HUS,
                                        label: Forsikring.HUS,
                                        onValueChange: setChecked,
                                        checked: isChecked(Forsikring.HUS),
                                    },
                                    {
                                        value: Forsikring.REISE,
                                        label: Forsikring.REISE,
                                        onValueChange: setChecked,
                                        checked: isChecked(Forsikring.REISE),
                                    },
                                    {
                                        value: Forsikring.HYTTE,
                                        label: Forsikring.HYTTE,
                                        onValueChange: setChecked,
                                        checked: isChecked(Forsikring.HYTTE),
                                    },
                                    {
                                        value: Forsikring.ANNET,
                                        label: Forsikring.ANNET,
                                        onValueChange: setChecked,
                                        checked: isChecked(Forsikring.ANNET),
                                    },
                                ]}
                            />
                            <FormFieldErrorMessage field="insurances" formik={formik} />
                        </div>
                    )}
                    {formik.values.type === NAERING && (
                        <div className="form-field__long">
                            <InputLabel
                                label="Hva ønsker du å forsikre?"
                                sublabel="Gi en kort beskrivelse av hva henvendelsen gjelder."
                            >
                                <ExpandableInput
                                    value={formik.values.insuranceDescription}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    minRows={14}
                                    name="insuranceDescription"
                                />
                                <FormFieldErrorMessage field="insuranceDescription" formik={formik} />
                            </InputLabel>
                        </div>
                    )}
                    {sentError && <ErrorSubmittingMessage />}
                    <div className="njff-buttons">
                        <StandardButton buttonType={ButtonTypes.STANDARD_NEGATIVE} onClick={onTilbakeClick}>
                            Tilbake
                        </StandardButton>
                        <ButtonWithSpinner
                            className="form-submit-button-with-spinner"
                            showSpinner={formik.isSubmitting}
                            buttonType={ButtonTypes.STANDARD_POSITIVE}
                            type="submit"
                        >
                            Send inn
                        </ButtonWithSpinner>
                    </div>
                </div>
            </div>
        </>
    );
};
export default Insurances;
